import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../services/api';

interface AnalyticsSummary {
  total_bookings: number;
  completed_bookings: number;
  cancelled_bookings: number;
  total_revenue: number;
  avg_rating: number;
  occupancy_rate: number;
}

interface DailyTrend {
  date: string;
  bookings: number;
  revenue: number;
  occupancy: number;
}

interface WeeklyTrend {
  week: string;
  bookings: number;
  revenue: number;
  occupancy: number;
}

interface PeakHour {
  hour: string;
  bookings: number;
  occupancy: number;
}

interface ActivityBreakdown {
  activity: string;
  bookings: number;
  revenue: number;
  percentage: number;
}

interface CustomerMetrics {
  total_customers: number;
  repeat_customers: number;
  repeat_rate: number;
  avg_bookings_per_customer: number;
  new_customers: number;
}

interface AnalyticsData {
  summary: AnalyticsSummary;
  trends: {
    daily_bookings: DailyTrend[];
    weekly_bookings: WeeklyTrend[];
  };
  peak_hours: PeakHour[];
  activity_breakdown: ActivityBreakdown[];
  customer_metrics: CustomerMetrics;
}

interface AnalyticsState {
  data: AnalyticsData | null;
  isLoading: boolean;
  error: string | null;
  selectedPeriod: string;
  dateRange: {
    from: string;
    to: string;
  };
}

const initialState: AnalyticsState = {
  data: null,
  isLoading: false,
  error: null,
  selectedPeriod: 'month',
  dateRange: {
    from: '',
    to: ''
  }
};

export const fetchAnalytics = createAsyncThunk(
  'analytics/fetchAnalytics',
  async ({ gymId, period, dateFrom, dateTo }: { gymId: string; period?: string; dateFrom?: string; dateTo?: string }, { rejectWithValue }) => {
    try {
      const params: any = {};
      if (period) params.period = period;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await apiClient.get(`/analytics/gyms/${gymId}/analytics`, { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

export const fetchSummary = createAsyncThunk(
  'analytics/fetchSummary',
  async ({ gymId, dateFrom, dateTo }: { gymId: string; dateFrom?: string; dateTo?: string }, { rejectWithValue }) => {
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await apiClient.get(`/analytics/gyms/${gymId}/analytics/summary`, { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch summary');
    }
  }
);

export const exportData = createAsyncThunk(
  'analytics/exportData',
  async ({ gymId, dateFrom, dateTo, dataType }: { gymId: string; dateFrom?: string; dateTo?: string; dataType: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/reports/export', {
        gym_id: gymId,
        date_from: dateFrom,
        date_to: dateTo,
        data_type: dataType
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response as any], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export data');
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setPeriod: (state, action: PayloadAction<string>) => {
      state.selectedPeriod = action.payload;
    },
    setDateRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
      state.dateRange = action.payload;
    },
    clearAnalytics: (state) => {
      state.data = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.data) {
          state.data.summary = action.payload;
        }
      })
      .addCase(fetchSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(exportData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exportData.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(exportData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setPeriod, setDateRange, clearAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;
